import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { cn } from '@/lib/cn';
import { PURCHASING_TABS, purchasingChord } from '@/features/buying/purchasing-tabs';

/**
 * The `?` cheatsheet, and the `g`-prefix jumps it documents.
 *
 * The status line advertises `?` on every screen, so it has to exist — a
 * console whose own rule is "an action either works or is absent" cannot ship
 * a dead key. This is also where the keyboard model gets written down once,
 * instead of living only in whoever built the screen's head.
 */

type Shortcut = { keys: string; label: string };
type Section = { title: string; items: Shortcut[] };

/**
 * `g <key>` jumps. Two-key sequences, 1.2s window, and they only fire when the
 * operator is not typing — a `g` in a search box must stay a `g`.
 */
export const GO_TARGETS: Record<string, { to: string; label: string }> = {
  t: { to: '/', label: 'Today' },
  o: { to: '/orders', label: 'Orders' },
  i: { to: '/inventory', label: 'Inventory' },
  c: { to: '/customers', label: 'Customers' },
  p: { to: '/purchasing', label: 'Purchasing' },
  r: { to: '/reports', label: 'Reports' },
  x: { to: '/transactions', label: 'Transactions' },
  v: { to: '/vendors', label: 'Vendors' },
  d: { to: '/products', label: 'Products' },
  s: { to: '/settings', label: 'Settings' },
};

const SECTIONS: Section[] = [
  {
    title: 'Anywhere',
    items: [
      { keys: '⌘K', label: 'Commands, and search every record' },
      { keys: '/', label: 'Jump straight to search' },
      { keys: '?', label: 'This cheatsheet' },
      { keys: 'Esc', label: 'Close, or clear the current selection' },
    ],
  },
  {
    title: 'Go to',
    items: Object.entries(GO_TARGETS).map(([key, t]) => ({
      keys: `g ${key}`,
      label: t.label,
    })),
  },
  {
    // UX-1 (TASK-0240): while in Purchasing, these win over the jumps above.
    title: 'In Purchasing',
    items: PURCHASING_TABS.map((tab) => ({ keys: `g ${tab.chord}`, label: tab.label })),
  },
  {
    title: 'In a list',
    items: [
      { keys: 'j  ↓', label: 'Next row' },
      { keys: 'k  ↑', label: 'Previous row' },
      { keys: '↵', label: 'Open the row under the cursor' },
      { keys: 'x', label: 'Select the row under the cursor' },
      { keys: '⌘A', label: 'Select every row on this page' },
      { keys: 'Esc', label: 'Clear the selection' },
    ],
  },
  {
    // TASK-0249 (INC-1c, useGridNav): the vendor guide and By product.
    title: 'On the order guide',
    items: [
      { keys: 'j  k', label: 'Next / previous line' },
      { keys: '↵', label: 'Into the line’s order box' },
      { keys: 'a', label: 'Accept the line’s suggestion' },
      { keys: 's', label: 'Every vendor of the item (Sources)' },
      { keys: '↓  ↑', label: 'In a box: the next / previous box' },
      { keys: 'Esc', label: 'Leave the box' },
    ],
  },
  {
    title: 'Screens with their own keys',
    items: [
      { keys: 'n', label: 'New — on Orders, Products, Vendors, Customers' },
      { keys: 'R', label: 'Toggle the sales rhythm range on Today' },
      { keys: '1 – 5', label: 'Switch report on Reports' },
      { keys: 'E', label: 'Export the current report as CSV' },
      { keys: '⌘S', label: 'Save an open editor' },
    ],
  },
];

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return (
    el.tagName === 'INPUT' ||
    el.tagName === 'TEXTAREA' ||
    el.tagName === 'SELECT' ||
    el.isContentEditable
  );
}

/**
 * Installs `?` and the `g`-prefix jumps, and renders the sheet.
 * Mounted once, by the shell.
 */
export function Cheatsheet() {
  const [open, setOpen] = useState(false);
  const [pendingGo, setPendingGo] = useState(false);
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    let goTimer: ReturnType<typeof setTimeout> | undefined;

    const onKey = (e: KeyboardEvent) => {
      // Never steal a key from a field, and never fight a modifier chord.
      if (isTyping(e.target) || e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
        return;
      }

      // `?` is shift+/ on most layouts; match the produced character, not the code.
      if (e.key === '?') {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }

      if (open) return;

      if (pendingGo) {
        const key = e.key.toLowerCase();
        // In Purchasing, g g / o / i / c / l open its tabs (UX-1).
        const target = purchasingChord(pathname, key) ?? GO_TARGETS[key];
        clearTimeout(goTimer);
        setPendingGo(false);
        if (target) {
          e.preventDefault();
          void navigate({ to: target.to });
        }
        return;
      }

      if (e.key === 'g') {
        e.preventDefault();
        setPendingGo(true);
        // Abandon the sequence rather than leaving the operator in a mode.
        goTimer = setTimeout(() => setPendingGo(false), 1200);
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      clearTimeout(goTimer);
    };
  }, [open, pendingGo, navigate, pathname]);

  const columns = useMemo(() => {
    // Two balanced columns so the sheet never needs scrolling on a laptop: the
    // global keys on the left, the in-context ones (Purchasing, lists, screens)
    // on the right.
    const mid = SECTIONS.findIndex((section) => section.title === 'In Purchasing');
    return [SECTIONS.slice(0, mid), SECTIONS.slice(mid)];
  }, []);

  return (
    <>
      {/* `g` is a mode, however briefly — say so, or it feels like a dropped key. */}
      {pendingGo ? (
        <div className="border-signal-line bg-surface-raised text-2xs text-ink pointer-events-none fixed bottom-8 left-1/2 z-50 -translate-x-1/2 rounded border px-2.5 py-1 font-mono shadow-lg">
          <span className="text-signal-ink">g</span> — go to…
        </div>
      ) : null}

      {open ? (
        <div
          className="fixed inset-0 z-[70] flex animate-[fade-in_100ms_ease-out] items-start justify-center bg-black/55 p-4 pt-[10vh] backdrop-blur-[1px]"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label="Keyboard shortcuts"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="border-line-strong bg-surface-raised w-full max-w-2xl animate-[pop-in_140ms_var(--ease-out-quint)] overflow-hidden rounded border shadow-2xl shadow-black/50"
          >
            <div className="border-line flex items-center gap-2 border-b px-4 py-2.5">
              <h2 className="text-ink text-sm font-semibold tracking-tight">Keyboard</h2>
              <span className="text-2xs text-ink-faint ml-auto font-mono">
                ? or esc to close
              </span>
            </div>

            <div className="scrollpane grid max-h-[70vh] grid-cols-1 gap-x-8 gap-y-5 p-4 sm:grid-cols-2">
              {columns.map((column, i) => (
                <div key={i} className="flex flex-col gap-5">
                  {column.map((section) => (
                    <section key={section.title}>
                      <h3 className="text-2xs text-ink-faint mb-1.5 font-medium tracking-widest uppercase">
                        {section.title}
                      </h3>
                      <dl className="flex flex-col">
                        {section.items.map((item) => (
                          <div
                            key={item.keys}
                            className="border-line-soft flex items-baseline gap-3 border-b py-1 last:border-0"
                          >
                            <dt className="w-16 shrink-0">
                              <kbd
                                className={cn(
                                  'border-line bg-surface-sunken inline-block rounded-xs border px-1.5 py-0.5',
                                  'text-2xs text-ink-muted font-mono'
                                )}
                              >
                                {item.keys}
                              </kbd>
                            </dt>
                            <dd className="text-ink-muted min-w-0 flex-1 text-xs">
                              {item.label}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </section>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
